/* stratos-artcrop.js  v2  (2026-08-12, client note round 3)
   Many product image FILES are square canvases with pure-white bands baked in
   (measured: 53 to 65px per side at 480px width). On the old white Avenue site
   the baked white blended into the page; on the linen ground it reads as a
   white margin around the artwork.

   v1 trimmed the bands by scaling the image up with negative margins. That
   removed the white but changed each work's apparent scale, which is what the
   owner reported on 2026-08-06: "the images appear in various sizes on a
   collection page which is very confusing".

   v2 keeps the same pixel measurement and changes only how the trim is
   applied. Card images are wrapped in a fit box whose aspect ratio is the
   TRIMMED artwork's ratio; the image is absolutely positioned inside it at
   exact percentages so the visible sub-rectangle maps onto the box edge to
   edge. The box then fits inside a fixed square holder, so every card shares
   one baseline while each work keeps its true proportions. The single large
   image on an artwork page is handled without touching the DOM, using
   object-fit and object-position, so the lightbox is untouched.

   No image file is ever modified. If any step fails the image is left exactly
   as served, letterboxed inside the holder by the stylesheet's contain rule. */
(function () {
  'use strict';
  var CARD_SEL = '#sxc-root .sxc-frame > img, .sxd-card-frame > img, .sxs-card-frame > img';
  var MAIN_SEL = '#sxd-main';
  var SEL = CARD_SEL + ', ' + MAIN_SEL;
  var MAXSIDE = 0.35;   /* never trim more than 35% from one side */
  var MINPAD = 0.04;    /* ignore bands thinner than 4%: real paper edges stay */

  function measure(probe) {
    var W = 120;
    var H = Math.max(1, Math.round(probe.naturalHeight * (W / probe.naturalWidth)));
    var c = document.createElement('canvas');
    c.width = W; c.height = H;
    var ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(probe, 0, 0, W, H);
    var d = ctx.getImageData(0, 0, W, H).data;
    function white(x, y) { var i = (y * W + x) * 4; return d[i] > 240 && d[i + 1] > 240 && d[i + 2] > 240; }
    function colFrac(x) { var n = 0, y; for (y = 0; y < H; y++) if (white(x, y)) n++; return n / H; }
    function rowFrac(y) { var n = 0, x; for (x = 0; x < W; x++) if (white(x, y)) n++; return n / W; }
    var l = 0; while (l < W * MAXSIDE && colFrac(l) > 0.92) l++;
    var r = 0; while (r < W * MAXSIDE && colFrac(W - 1 - r) > 0.92) r++;
    var t = 0; while (t < H * MAXSIDE && rowFrac(t) > 0.92) t++;
    var b = 0; while (b < H * MAXSIDE && rowFrac(H - 1 - b) > 0.92) b++;
    if ((l + r) < W * MINPAD) { l = 0; r = 0; }
    if ((t + b) < H * MINPAD) { t = 0; b = 0; }
    return { W: W, H: H, l: l, r: r, t: t, b: b };
  }

  /* ---- card images: exact trim inside a fit box ---- */

  function fitBoxFor(img) {
    var p = img.parentNode;
    if (p && p.className === 'sx-fit') return p;
    var box = document.createElement('span');
    box.className = 'sx-fit';
    p.insertBefore(box, img);
    box.appendChild(img);
    return box;
  }

  function applyCard(img, m) {
    var visW = m.W - m.l - m.r;
    var visH = m.H - m.t - m.b;
    if (visW < m.W * 0.3 || visH < m.H * 0.3) return;
    var box = fitBoxFor(img);
    box.style.aspectRatio = visW + ' / ' + visH;
    /* The holder is square, so pin the longer axis to 100% and let the ratio
       derive the other. An auto/auto box whose only child is absolutely
       positioned has no intrinsic size and would collapse to zero. */
    if (visW >= visH) { box.style.width = '100%'; box.style.height = 'auto'; }
    else { box.style.height = '100%'; box.style.width = 'auto'; }
    /* percentages resolve against the box: width/left against its width,
       height/top against its height, so the mapping is exact on both axes */
    img.style.position = 'absolute';
    img.style.width = (100 * m.W / visW) + '%';
    img.style.height = (100 * m.H / visH) + '%';
    img.style.left = (-100 * m.l / visW) + '%';
    img.style.top = (-100 * m.t / visH) + '%';
    img.style.maxWidth = 'none';
    img.style.maxHeight = 'none';
    img.style.objectFit = 'fill';
    img.dataset.sxCropped = '1';
    /* Susanna 2026-08-03 note 6: "the title/description underneath the image is
       way off to the left". Inside a uniform holder a narrow work is centred,
       so left-aligned caption text no longer starts at the artwork's edge.
       Indent the caption by exactly the holder's side inset, as a percentage of
       the card so it holds at every width. */
    alignMeta(img);
    /* blank guard: if anything about the ratio box failed to resolve, put the
       image back exactly as served rather than shipping an empty frame */
    if (box.getBoundingClientRect().width < 4) { resetCard(img); }
  }

  function frameOf(img) {
    var n = img.parentNode;
    while (n && n.className !== undefined) {
      if (/(^|\s)(sxc-frame|sxd-card-frame|sxs-card-frame)(\s|$)/.test(n.className || '')) return n;
      n = n.parentNode;
    }
    return null;
  }

  function metaFor(imgOrBox) {
    var frame = frameOf(imgOrBox.tagName === 'IMG' ? imgOrBox : (imgOrBox.querySelector('img') || imgOrBox));
    if (!frame) return null;
    var meta = frame.nextElementSibling;
    if (!meta) return null;
    return /-meta(\s|$)/.test(' ' + (meta.className || '')) ? meta : null;
  }

  /* Measures what is actually rendered rather than assuming a crop happened,
     so an image with no measurable bands is aligned the same way as a
     trimmed one. The inset is stored as a percentage of the holder, so it
     holds at every viewport width without a resize listener. */
  function alignMeta(img) {
    try {
      var frame = frameOf(img);
      var meta = metaFor(img);
      if (!frame || !meta) return;
      var boxEl = (img.parentNode && img.parentNode.className === 'sx-fit') ? img.parentNode : img;
      var fw = frame.getBoundingClientRect().width;
      var bw = boxEl.getBoundingClientRect().width;
      if (!fw || !bw) return;
      var inset = (fw - bw) / 2 / fw * 100;
      if (inset > 0.5) {
        meta.style.paddingLeft = inset + '%';
        meta.style.paddingRight = inset + '%';
      } else {
        meta.style.paddingLeft = '';
        meta.style.paddingRight = '';
      }
    } catch (e) {}
  }

  function resetCard(img) {
    if (!img.dataset.sxCropped) return;
    img.dataset.sxCropped = '';
    var p = img.parentNode;
    if (p && p.className === 'sx-fit') {
      p.style.aspectRatio = ''; p.style.width = ''; p.style.height = '';
      var mt = metaFor(img);
      if (mt) { mt.style.paddingLeft = ''; mt.style.paddingRight = ''; }
    }
    img.style.position = '';
    img.style.width = '';
    img.style.height = '';
    img.style.left = '';
    img.style.top = '';
    img.style.maxWidth = '';
    img.style.maxHeight = '';
    img.style.objectFit = '';
  }

  /* ---- the single large artwork image: no DOM change, lightbox untouched ---- */

  function applyMain(img, m) {
    var padX = m.l + m.r;
    var padY = m.t + m.b;
    if (!padX && !padY) return;
    var visW = m.W - padX;
    var visH = m.H - padY;
    if (visW < m.W * 0.3 || visH < m.H * 0.3) return;
    var px = padX ? (100 * m.l / padX) : 50;
    var py = padY ? (100 * m.t / padY) : 50;
    img.style.aspectRatio = visW + ' / ' + visH;
    img.style.objectFit = 'cover';
    img.style.objectPosition = px + '% ' + py + '%';
    /* holder is 4/5; pin whichever axis keeps the work inside it and fills */
    if ((visW / visH) >= 0.8) { img.style.width = '100%'; img.style.height = 'auto'; }
    else { img.style.height = '100%'; img.style.width = 'auto'; }
    img.style.maxWidth = '100%';
    img.style.maxHeight = '100%';
    img.dataset.sxCropped = '1';
    if (img.getBoundingClientRect().width < 4) { resetMain(img); }
  }

  function resetMain(img) {
    if (!img.dataset.sxCropped) return;
    img.dataset.sxCropped = '';
    img.style.aspectRatio = '';
    img.style.objectFit = '';
    img.style.objectPosition = '';
    img.style.width = '';
    img.style.height = '';
    img.style.maxWidth = '';
    img.style.maxHeight = '';
  }

  function isMain(img) { return img.id === 'sxd-main'; }

  function crop(img) {
    try {
      var src = img.currentSrc || img.src;
      if (!src) return;
      if (img.dataset.sxCropSrc === src) return;
      img.dataset.sxCropSrc = src;
      var probe = new Image();
      probe.crossOrigin = 'anonymous';
      probe.onload = function () {
        try {
          var m = measure(probe);
          if (isMain(img)) { resetMain(img); applyMain(img, m); }
          else { resetCard(img); applyCard(img, m); }
        } catch (e) {}
      };
      probe.src = src;
    } catch (e) {}
  }

  function prep(img) {
    if (img.dataset.sxCropBound) return;
    img.dataset.sxCropBound = '1';
    if (img.complete && img.naturalWidth) { crop(img); if (!isMain(img)) alignMeta(img); }
    img.addEventListener('load', function () { crop(img); if (!isMain(img)) alignMeta(img); });
  }

  function scan(root) {
    var list = (root || document).querySelectorAll(SEL);
    for (var i = 0; i < list.length; i++) prep(list[i]);
  }

  window.sxArtcropScan = scan;
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scan(); });
  } else {
    scan();
  }
})();
